import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { ProductSchedule } from '@nucleus-commerce/api-client/admin';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@nucleus-commerce/ui/components/alert-dialog';
import { Checkbox } from '@nucleus-commerce/ui/components/checkbox';
import { Label } from '@nucleus-commerce/ui/components/label';
import { toast } from '@nucleus-commerce/ui/components/sonner';

import { useDeleteSchedule } from '../hooks/use-schedule-mutations';

import { ScheduleErrorAlert } from './schedule-error-alert';

export interface DeleteScheduleDialogProps {
  schedule: ProductSchedule;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onDeleted: () => void;
}

/**
 * Deletes a draft or ended schedule. Its store products stay in their stores as they are, except
 * that by default the drafts it added and that were never published go with it.
 */
export function DeleteScheduleDialog({
  schedule,
  open,
  onOpenChange,
  onDeleted,
}: DeleteScheduleDialogProps) {
  const { t } = useTranslation(['schedules', 'common']);
  const remove = useDeleteSchedule();
  const [removeDrafts, setRemoveDrafts] = useState(true);
  return (
    <AlertDialog
      open={open}
      onOpenChange={(next) => {
        if (!next) {
          remove.reset();
          setRemoveDrafts(true);
        }
        onOpenChange(next);
      }}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            {t('schedules:delete.title', { name: schedule.name })}
          </AlertDialogTitle>
          <AlertDialogDescription>{t('schedules:delete.description')}</AlertDialogDescription>
        </AlertDialogHeader>
        <div className="flex items-start gap-2.5">
          <Checkbox
            id="schedule-delete-drafts"
            checked={removeDrafts}
            disabled={remove.isPending}
            onCheckedChange={(checked) => setRemoveDrafts(checked === true)}
            className="mt-0.5"
          />
          <Label htmlFor="schedule-delete-drafts" className="leading-snug font-normal">
            {t('schedules:delete.removeDrafts')}
          </Label>
        </div>
        {remove.isError ? <ScheduleErrorAlert error={remove.error} action="delete" /> : null}
        <AlertDialogFooter>
          <AlertDialogCancel disabled={remove.isPending}>
            {t('common:actions.cancel')}
          </AlertDialogCancel>
          <AlertDialogAction
            disabled={remove.isPending}
            className="bg-destructive hover:bg-destructive/90 text-white"
            onClick={(event) => {
              event.preventDefault();
              remove.mutate(
                { id: schedule.id, removeDrafts },
                {
                  onSuccess: (answer) => {
                    toast.success(
                      answer
                        ? t('schedules:toasts.deletedWithDrafts', { count: answer.removedDrafts })
                        : t('schedules:toasts.deleted'),
                    );
                    onOpenChange(false);
                    onDeleted();
                  },
                },
              );
            }}
          >
            {t('common:actions.delete')}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
